/**
 * Screen registry for every artboard on the "Warehouse Platform — Core Screens" canvas,
 * the UI master for apps/web (desktop + tablet station mode) and apps/mobile (phone).
 *
 * registry.json is GENERATED by scripts/canvas/extract_registry.py from the canvas files.
 * Do not hand-edit it; re-run the extractor when the canvas changes.
 *
 * Status vocabulary (CLAUDE.md): a screen with `wiredWeb`/`wiredMobile` maps to an existing
 * implemented page; every other screen is a UI shell with sample data, not a working workflow.
 */
import data from './registry.json';

export type Device = 'web' | 'phone' | 'print' | 'tv';

export type StationKind =
  | 'receiving'
  | 'pack'
  | 'dock'
  | 'print'
  | 'supervisor'
  | 'cruise'
  | 'kitting';

export interface HeaderAction {
  label: string;
  primary: boolean;
}

export interface NavLink {
  label: string;
  board: string;
}

export interface Screen {
  /** Canvas artboard file stem, unique, e.g. "SCR034" or "Receiving". */
  board: string;
  /** Canvas screen id, e.g. "SCR-034", "DSH-01"; null for a few support boards. */
  id: string | null;
  name: string;
  heading: string;
  /** Route the canvas specifies (e.g. "/purchasing/purchase-orders-list"), if any. */
  route: string | null;
  /** Stable fallback path, "/s/<board-slug>". */
  path: string;
  priority: 'P0' | 'P1' | 'P2' | null;
  /** Layout family from the canvas (TABLE, SPLIT, SCAN, FORM, BOARD, CTRL, ...). */
  layout: string;
  device: Device;
  width: number;
  height: number;
  page: string;
  pageName: string;
  module: string | null;
  breadcrumb: string | null;
  fields: string[];
  actions: string[];
  columns: string[];
  headerActions: HeaderAction[];
  controls: string[];
  nav: NavLink[];
  station: StationKind | null;
  /** Existing apps/web route that already implements this screen. */
  wiredWeb: string | null;
  /** Existing apps/mobile navigator screen name that already implements this screen. */
  wiredMobile: string | null;
}

export interface CanvasPage {
  id: string;
  name: string;
}

interface RegistryFile {
  source: { artifact: string; title: string; boards: number };
  pages: CanvasPage[];
  stations: Record<StationKind, string[]>;
  screens: Screen[];
}

const registry = data as unknown as RegistryFile;

export const canvasSource = registry.source;
export const canvasPages: CanvasPage[] = registry.pages;
export const screens: Screen[] = registry.screens;

/** Boards that document the platform itself; they are not product screens. */
export const DOC_BOARDS = new Set([
  'Catalog',
  'Coverage',
  'DesignSystem',
  'PatternCoverage',
  'TopBar',
  'TopNav',
  'UXResearch',
]);

const byBoardMap = new Map(screens.map((s) => [s.board, s]));

export function screenByBoard(board: string): Screen | undefined {
  return byBoardMap.get(board);
}

/** The path the web app serves a screen at: the canvas route when unique, else /s/<slug>. */
export function webPath(s: Screen): string {
  if (s.wiredWeb && !s.wiredWeb.includes(':')) return s.wiredWeb;
  return s.route && !duplicateRoutes.has(s.route) ? s.route : s.path;
}

const routeCounts = new Map<string, number>();
for (const s of screens) {
  if (s.route) routeCounts.set(s.route, (routeCounts.get(s.route) ?? 0) + 1);
}
const duplicateRoutes = new Set([...routeCounts].filter(([, n]) => n > 1).map(([r]) => r));

/** Product screens shown in web navigation (web + tv + print previews). */
export const webScreens = screens.filter((s) => s.device !== 'phone' && !DOC_BOARDS.has(s.board));
/** Native phone screens for apps/mobile. */
export const phoneScreens = screens.filter((s) => s.device === 'phone');
/** Tablet station screens for apps/web station mode, grouped by station. */
export const stationScreens = screens.filter((s) => s.station !== null);
export const stations = registry.stations;

export interface NavGroup {
  id: string;
  label: string;
  pages: string[];
}

/** The seven web navigation groups from the E2E plan, mapped to canvas pages. */
export const navGroups: NavGroup[] = [
  { id: 'operate', label: 'Operate', pages: ['home', 'm01', 'ux', 'm05', 'm06', 'm07', 'm08', 'm09', 'm10', 'm11', 'm12', 'm13', 'm14', 'm19'] },
  { id: 'cruise', label: 'Cruise', pages: ['m21', 'xch'] },
  { id: 'commercial', label: 'Commercial', pages: ['m15', 'm20', 'quotes', 'quoting', 'm03', 'm04', 'm24', 'm02'] },
  { id: 'money', label: 'Money', pages: ['books', 'm16'] },
  { id: 'insight', label: 'Insight', pages: ['dash', 'm18', 'ai'] },
  { id: 'workspace', label: 'Workspace', pages: ['label', 'bt', 'mail', 'cal', 'sheets', 'maps', 'm17'] },
  { id: 'admin', label: 'Admin', pages: ['m22', 'system'] },
];

export function screensOnPage(pageId: string, device?: Device): Screen[] {
  return screens.filter((s) => s.page === pageId && (!device || s.device === device));
}

/**
 * Deterministic sample rows for a shell, built from the screen's columns or fields.
 * Values are clearly placeholder text, never realistic-looking operational data.
 */
export function sampleRows(s: Screen, count = 6): Record<string, string>[] {
  const cols = (s.columns.length ? s.columns : s.fields).slice(0, 8);
  return Array.from({ length: count }, (_, i) =>
    Object.fromEntries(cols.map((c, j) => [c, j === 0 ? `Sample ${i + 1}` : '—'])),
  );
}

/** Columns a shell shows: canvas table headers first, else registry fields. */
export function shellColumns(s: Screen): string[] {
  return (s.columns.length ? s.columns : s.fields).slice(0, 8);
}
